const TRAILING_SLASHES_PATTERN = /\/+$/
const ADMIN_ROUTE_SEGMENTS_PATTERN = /^\/([^/]+)\/(.+)$/
const ORGANIZATION_RESOURCE_PATTERN = /^\/organizations\/[^/]+\/([^/?]+)/
const ORGANIZATION_PREFIX_PATTERN = /^\/organizations\/[^/]+(?=\/)/

export const ADMIN_API_BASE_PATH = '/api/admin/v1'
export const LEGACY_ADMIN_API_BASE_PATH = '/api/v1'

const NON_ORG_PREFIXES = ['auth', 'organizations', 'notifications', 'command-center', 'platform']
const ORG_SCOPED_PREFIXES = [
  'resources',
  'posts',
  'forms',
  'users',
  'approvals',
  'creators',
  'provider-info',
  'selections',
  'kambi',
  'og',
  'tags',
  'media',
  'media-assets',
  'media-templates',
  'media-schedules',
  'legs',
  'analytics',
  'growth',
  'experiments',
  'feed',
  'feed-ops',
  'products',
  'igaming',
  'sports-search',
  'native-sportsbook',
  'ai',
  'surfaces',
  'prepacks',
  'affiliate-links',
  'market-insights',
  'moderation',
]

function splitPathAndQuery(url = '') {
  const queryIndex = url.indexOf('?')
  if (queryIndex === -1) {
    return { path: url, query: '' }
  }

  return {
    path: url.slice(0, queryIndex),
    query: url.slice(queryIndex),
  }
}

function ensureRelativeAdminPath(path) {
  if (path.startsWith(`${ADMIN_API_BASE_PATH}/`)) {
    return path.slice(ADMIN_API_BASE_PATH.length)
  }
  if (path === ADMIN_API_BASE_PATH) {
    return '/'
  }
  if (path.startsWith(`${LEGACY_ADMIN_API_BASE_PATH}/`)) {
    return path.slice(LEGACY_ADMIN_API_BASE_PATH.length)
  }
  if (path === LEGACY_ADMIN_API_BASE_PATH) {
    return '/'
  }
  return path
}

export function getAdminApiBaseUrl(apiUrl = '') {
  const normalizedApiUrl = apiUrl.replace(TRAILING_SLASHES_PATTERN, '')
  return normalizedApiUrl ? `${normalizedApiUrl}${ADMIN_API_BASE_PATH}` : ADMIN_API_BASE_PATH
}

export function canonicalizeAdminRequestPath(url = '') {
  if (!url || !url.startsWith('/')) {
    return url
  }

  const { path, query } = splitPathAndQuery(url)
  const relativePath = ensureRelativeAdminPath(path)

  if (!relativePath.startsWith('/')) {
    return url
  }

  if (
    relativePath === '/' ||
    relativePath.startsWith('/auth/') ||
    relativePath.startsWith('/notifications/') ||
    relativePath.startsWith('/command-center/') ||
    relativePath.startsWith('/organizations/')
  ) {
    return `${relativePath}${query}`
  }

  const match = relativePath.match(ADMIN_ROUTE_SEGMENTS_PATTERN)
  if (!match) {
    return `${relativePath}${query}`
  }

  const [, orgSlug, resourcePath] = match
  if (NON_ORG_PREFIXES.includes(orgSlug)) {
    return `${relativePath}${query}`
  }

  const resource = resourcePath.split('/')[0]
  if (!ORG_SCOPED_PREFIXES.some(prefix => resource.startsWith(prefix))) {
    return `${relativePath}${query}`
  }

  return `/organizations/${orgSlug}/${resourcePath}${query}`
}

function isCanonicalOrganizationScopedPath(path) {
  const match = path.match(ORGANIZATION_RESOURCE_PATTERN)
  if (!match) {
    return false
  }

  const resource = match[1]
  return ORG_SCOPED_PREFIXES.some(prefix => resource.startsWith(prefix))
}

export function normalizeAdminRequestPath(url = '', currentOrgSlug = '', skipOrgRewrite = false) {
  const canonicalPath = canonicalizeAdminRequestPath(url)
  if (skipOrgRewrite || !currentOrgSlug || !isCanonicalOrganizationScopedPath(canonicalPath)) {
    return canonicalPath
  }

  return canonicalPath.replace(ORGANIZATION_PREFIX_PATTERN, `/organizations/${currentOrgSlug}`)
}
